import React from "react";
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip,
  Stack,
  TextField,
  Chip,
  ListItemText,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getStudies, getStudiesBySponsor } from "../../redux/actions/study";
import { getCenters } from "../../redux/actions/center";
import { getSponsors } from "../../redux/actions/sponsor";
import { instance } from "../../axios";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";

export const organizationOptions = ["CDL", "Sponsor", "Center"];

export const ROLE_MAP = {
  CDL: [
    { value: "admin", label: "Admin" },
    { value: "technician", label: "Technician" },
    { value: "employee", label: "Employee" },
  ],
  Sponsor: [
    { value: "manager", label: "Manager" },
    { value: "monitor", label: "Monitor" },
  ],
  Center: [
    { value: "manager", label: "Manager" },
    { value: "employee", label: "Employee" },
  ],
};

export const getRoleOptions = (org) => ROLE_MAP[org] || [];

export default function OrganizeDetails({ user, onUpdated }) {
  const dispatch = useDispatch();
  const studies = useSelector((s) => s.study.studies) || [];
  const centers = useSelector((s) => s.center.centers) || [];
  const sponsors = useSelector((s) => s.sponsor.sponsors) || [];

  const [editing, setEditing] = React.useState(false);
  const [organization, setOrganization] = React.useState(
    user?.organization || ""
  );
  const [role, setRole] = React.useState(user?.role || "");
  const [sponsorId, setSponsorId] = React.useState(user?.sponsorId || "");
  const [studyId, setStudyId] = React.useState(user?.studyId || "");
  const [centerId, setCenterId] = React.useState(user?.centerId || "");
  const [checkedStudies, setCheckedStudies] = React.useState(
    user?.checkedStudies || []
  );
  const [checkedCenters, setCheckedCenters] = React.useState(
    user?.checkedCenters || []
  );
  const [saving, setSaving] = React.useState(false);

  const roleOptions = getRoleOptions(organization);

  // sync from parent
  React.useEffect(() => {
    setOrganization(user?.organization || "");
    setRole(user?.role || "");
    setSponsorId(user?.sponsorId || "");
    setStudyId(user?.studyId || "");
    setCenterId(user?.centerId || "");
    setCheckedStudies(user?.checkedStudies || []);
    setCheckedCenters(user?.checkedCenters || []);
  }, [user]);

  // fetch lists
  React.useEffect(() => {
    if (organization === "Sponsor") dispatch(getSponsors());
  }, [organization, dispatch]);

  React.useEffect(() => {
    if (sponsorId) dispatch(getStudiesBySponsor(sponsorId));
    else dispatch(getStudies());
  }, [sponsorId, dispatch]);

  React.useEffect(() => {
    if (studyId) dispatch(getCenters(studyId));
  }, [studyId, dispatch]);

  // reset deps on org change
  React.useEffect(() => {
    setCenterId("");
    setSponsorId("");
    setCheckedCenters([]);
    setCheckedStudies([]);
    setStudyId("");
    if (!roleOptions.some((r) => r.value === role)) {
      setRole(roleOptions[0]?.value || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  const handleSave = async () => {
    if (!user?.id) return;
    setSaving(true);
    try {
      const payload = {
        organization,
        role,
        sponsorId: sponsorId || null,
        studyId: studyId || null,
        centerId: centerId || null,
        checkedStudies,
        checkedCenters,
      };
      const res = await instance.put(`/users/${user.id}/organize`, payload);
      const updated = res.data || {};
      setOrganization(updated.organization ?? payload.organization);
      setRole(updated.role ?? payload.role);
      setSponsorId(updated.sponsorId ?? payload.sponsorId);
      setStudyId(updated.studyId ?? payload.studyId);
      setCenterId(updated.centerId ?? payload.centerId);
      setCheckedStudies(
        Array.isArray(updated.checkedStudies)
          ? updated.checkedStudies
          : payload.checkedStudies || []
      );
      setCheckedCenters(
        Array.isArray(updated.checkedCenters)
          ? updated.checkedCenters
          : payload.checkedCenters || []
      );
      setEditing(false);
      if (typeof onUpdated === "function") onUpdated(updated);
    } catch (err) {
      console.error("update organize failed", err);
    } finally {
      setSaving(false);
    }
  };

  // shared styles
  const baseFieldSx = {
    "& .MuiInputBase-root": {
      height: 48,
    },
  };

  const showFieldSx = {
    ...baseFieldSx,
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "rgba(0,0,0,0.05)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(0,0,0,0.05)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "rgba(0,0,0,0.05)",
      },
    },
  };

  const editFieldSx = {
    ...baseFieldSx,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(0,0,0,0.35)",
    },
  };

  const footerButtonStyle = {
    height: 36,
    minHeight: 36,
    display: "flex",
    alignItems: "center",
  };

  const sponsorName =
    (sponsors.find((s) => s.id === sponsorId) || {}).name || sponsorId || "";
  const studyName =
    (studies.find((s) => s.id === studyId) || {}).cdlStudyCode || studyId || "";
  const centerName =
    (centers.find((c) => c.id === centerId) || {}).centerNumber ||
    centerId ||
    "";

  return (
    <>
      <Typography variant="h5" gutterBottom sx={{ mt: 0 }}>
        Organization
      </Typography>

      <Box
        sx={{
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1.5,
          backgroundColor: "background.paper",
          width: 600,
          maxWidth: "90vw",
        }}
      >
        {!editing ? (
          // ---------------- SHOW MODE ----------------
          <>
            <Grid container spacing={2} sx={{ mt: 0.2 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Organization"
                  value={organization || ""}
                  variant="outlined"
                  fullWidth
                  size="small"
                  sx={showFieldSx}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Role"
                  value={role || ""}
                  variant="outlined"
                  fullWidth
                  size="small"
                  sx={showFieldSx}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              {sponsorId ? (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Sponsor"
                    value={sponsorName}
                    variant="outlined"
                    fullWidth
                    size="small"
                    sx={showFieldSx}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              ) : null}

              {studyId && organization !== "Sponsor" ? (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Study"
                    value={studyName}
                    variant="outlined"
                    fullWidth
                    size="small"
                    sx={showFieldSx}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              ) : null}

              {centerId ? (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Center"
                    value={centerName}
                    variant="outlined"
                    fullWidth
                    size="small"
                    sx={showFieldSx}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              ) : null}

              {organization === "Sponsor" &&
              Array.isArray(checkedStudies) &&
              checkedStudies.length > 0 ? (
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Studies
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {checkedStudies.map((id) => {
                      const study = studies.find((s) => s.id === id) || {
                        cdlStudyCode: id,
                      };
                      return (
                        <Chip
                          key={id}
                          label={study.cdlStudyCode}
                          size="small"
                          sx={{ backgroundColor: "action.hover" }}
                        />
                      );
                    })}
                  </Box>
                </Grid>
              ) : null}

              {Array.isArray(checkedCenters) && checkedCenters.length > 0 ? (
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Centers
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {checkedCenters.map((id) => {
                      const center = centers.find((c) => c.id === id) || {
                        centerNumber: id,
                      };
                      return (
                        <Chip
                          key={id}
                          label={center.centerNumber}
                          size="small"
                          sx={{ backgroundColor: "action.hover" }}
                        />
                      );
                    })}
                  </Box>
                </Grid>
              ) : null}
            </Grid>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 2,
                minHeight: 40,
              }}
            >
              <Tooltip title="Edit role / organization">
                <IconButton
                  onClick={() => setEditing(true)}
                  aria-label="edit organization"
                  sx={footerButtonStyle}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <span style={{ width: 120, height: 36 }} />
            </Box>
          </>
        ) : (
          // ---------------- EDIT MODE ----------------
          <>
            <Grid container spacing={2} sx={{ mt: 0.2 }}>
              {/* Organization */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" sx={editFieldSx}>
                  <InputLabel id="org-select">Organization</InputLabel>
                  <Select
                    labelId="org-select"
                    value={organization}
                    label="Organization"
                    onChange={(e) => {
                      const v = e.target.value;
                      setOrganization(v);
                      setRole("");
                      setStudyId("");
                      setCheckedCenters([]);
                      setCheckedStudies([]);
                      setCenterId("");
                      setSponsorId("");
                    }}
                  >
                    {organizationOptions.map((o) => (
                      <MenuItem key={o} value={o}>
                        {o}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Role */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" sx={editFieldSx}>
                  <InputLabel id="role-select">Role</InputLabel>
                  <Select
                    labelId="role-select"
                    value={role}
                    label="Role"
                    onChange={(e) => setRole(e.target.value)}
                  >
                    {roleOptions.map((r) => (
                      <MenuItem key={r.value} value={r.value}>
                        {r.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Sponsor path */}
              {organization === "Sponsor" && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small" sx={editFieldSx}>
                    <InputLabel id="sponsor-select">Sponsor</InputLabel>
                    <Select
                      labelId="sponsor-select"
                      value={sponsorId}
                      label="Sponsor"
                      onChange={(e) => {
                        setSponsorId(e.target.value);
                        setStudyId("");
                        setCheckedCenters([]);
                        setCheckedStudies([]); // clear old studies when sponsor changes
                      }}
                    >
                      {Array.isArray(sponsors)
                        ? sponsors.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                              {s.name}
                            </MenuItem>
                          ))
                        : null}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Sponsor always sees multi-select studies */}
              {organization === "Sponsor" && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small" sx={editFieldSx}>
                    <InputLabel id="studies-multi">Studies</InputLabel>
                    <Select
                      labelId="studies-multi"
                      multiple
                      value={checkedStudies}
                      label="Studies"
                      onChange={(e) => {
                        const value = e.target.value;
                        setCheckedStudies(
                          typeof value === "string" ? value.split(",") : value
                        );
                      }}
                      renderValue={(selected) => {
                        if (!studies.length) return "";
                        if (!selected || selected.length === 0) return "";
                        return selected
                          .map((id) => {
                            const st = studies.find((s) => s.id === id);
                            return st ? st.cdlStudyCode : id;
                          })
                          .join(", ");
                      }}
                      disabled={!studies.length}
                      MenuProps={{
                        PaperProps: {
                          style: { maxHeight: 300 },
                        },
                      }}
                    >
                      {studies.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          <Checkbox
                            checked={checkedStudies.indexOf(s.id) > -1}
                          />
                          <ListItemText primary={s.cdlStudyCode} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* non-sponsor path */}
              {organization !== "Sponsor" &&
                (organization === "Center" || role === "monitor") && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" sx={editFieldSx}>
                        <InputLabel id="study-select">Study</InputLabel>
                        <Select
                          labelId="study-select"
                          value={studyId}
                          label="Study"
                          onChange={(e) => {
                            setStudyId(e.target.value);
                            setCenterId("");
                          }}
                        >
                          {studies.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                              {s.cdlStudyCode}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {studyId && organization === "Center" && (
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small" sx={editFieldSx}>
                          <InputLabel id="center-select">Center</InputLabel>
                          <Select
                            labelId="center-select"
                            value={centerId}
                            label="Center"
                            onChange={(e) => setCenterId(e.target.value)}
                          >
                            {centers.map((c) => (
                              <MenuItem key={c.id} value={c.id}>
                                {c.centerNumber}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                  </>
                )}

              {organization !== "Sponsor" &&
                ((role === "manager" && sponsorId) ||
                  (organization === "CDL" && role === "employee")) && (
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Studies
                    </Typography>
                    <FormGroup row sx={{ gap: 1, flexWrap: "wrap" }}>
                      {studies.map((s) => (
                        <FormControlLabel
                          key={s.id}
                          control={
                            <Checkbox
                              checked={checkedStudies.includes(s.id)}
                              onChange={(ev) => {
                                if (ev.target.checked)
                                  setCheckedStudies([...checkedStudies, s.id]);
                                else
                                  setCheckedStudies(
                                    checkedStudies.filter((i) => i !== s.id)
                                  );
                              }}
                            />
                          }
                          label={s.cdlStudyCode}
                        />
                      ))}
                    </FormGroup>
                  </Grid>
                )}

              {organization !== "Sponsor" &&
                role === "monitor" &&
                sponsorId &&
                studyId && (
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Centers
                    </Typography>
                    <FormGroup row sx={{ gap: 1, flexWrap: "wrap" }}>
                      {centers.map((c) => (
                        <FormControlLabel
                          key={c.id}
                          control={
                            <Checkbox
                              checked={checkedCenters.includes(c.id)}
                              onChange={(ev) => {
                                if (ev.target.checked)
                                  setCheckedCenters([...checkedCenters, c.id]);
                                else
                                  setCheckedCenters(
                                    checkedCenters.filter((i) => i !== c.id)
                                  );
                              }}
                            />
                          }
                          label={c.centerNumber}
                        />
                      ))}
                    </FormGroup>
                  </Grid>
                )}
            </Grid>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 2,
                minHeight: 40,
              }}
            >
              <Stack direction="row" spacing={1} sx={footerButtonStyle}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                  sx={footerButtonStyle}
                >
                  {saving ? "Saving..." : "Save"}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CloseIcon />}
                  onClick={() => {
                    setOrganization(user?.organization || "");
                    setRole(user?.role || "");
                    setCenterId(user?.centerId || "");
                    setSponsorId(user?.sponsorId || "");
                    setStudyId(user?.studyId || "");
                    setCheckedStudies(user?.checkedStudies || []);
                    setCheckedCenters(user?.checkedCenters || []);
                    setEditing(false);
                  }}
                  sx={footerButtonStyle}
                >
                  Cancel
                </Button>
              </Stack>
              <span style={{ width: 120, height: 36 }} />
            </Box>
          </>
        )}
      </Box>
    </>
  );
}
